/**
 * What a running Ion Next code execution is doing, read from its code.
 *
 * The backend announces a code call as soon as the model starts writing it
 * (status "Writing code…", empty input); the full input follows while the
 * code runs. Admins receive that input unredacted, so the label can say what
 * the code is for instead of "code_execution". Pure, no I/O.
 *
 * The wire input is `{sub_tool, ...}`: `command` for bash, `code` for a
 * programmatic run, `command` + `path` + `file_text` / `new_str` for the text
 * editor. Long strings may arrive trimmed in the middle (backend
 * `files.trim_strings` keeps head and tail), so the imports at the top still
 * decide the label.
 *
 * Library names follow the sandbox brand kit
 * (backend/app/api/v1/chat/native/brand_kit/README.md): `bb.pptx_presentation`,
 * `bb.docx_document`, `bb.Report(...)` + `rep.build`, `bb.use_matplotlib`,
 * `bb.save_figure`; files land in $OUTPUT_DIR via `bb.output_path("x.pptx")`.
 */

// First match wins: a report or deck usually plots a chart too.
const RULES = [
  { label: 'Creating presentation', test: /pptx|\bPresentation\(|\badd_slide\(/ },
  { label: 'Creating Word document', test: /docx/ },
  { label: 'Creating PDF report', test: /reportlab|\bReport\(/ },
  { label: 'Plotting chart', test: /matplotlib|\bplt\.|\bsave_figure\(/ },
  { label: 'Building spreadsheet', test: /openpyxl|\.xlsx\b|\bto_excel\(/ },
]

const FILE_LITERAL = /["']([^"'\r\n]*?\.(?:pptx|docx|pdf|png|xlsx|csv))["']/i

function codeOf(input) {
  return ['code', 'command', 'file_text', 'new_str']
    .map((k) => input[k])
    .filter((v) => typeof v === 'string' && v)
    .join('\n')
}

/**
 * @param {object} input — the code_execution call's wire input
 * @returns {{label: string, file: string|null}|null} null when there is no
 *   code yet (the caller shows the start status instead)
 */
export function ionNextCodeStatus(input) {
  if (!input || typeof input !== 'object') return null
  const code = codeOf(input)
  if (!code.trim()) return null

  const match = FILE_LITERAL.exec(code)
  const file = match ? match[1].split(/[\\/]/).pop() || null : null

  const rule = RULES.find((r) => r.test.test(code))
  if (rule) return { label: rule.label, file }
  if (input.sub_tool === 'text_editor_code_execution' && input.command === 'create') {
    return { label: 'Writing file', file }
  }
  return { label: 'Analysing data', file }
}
